import { create } from 'zustand';
import leadService from '../services/leadService';
import useUIStore from './uiStore';

const useLeadStore = create((set, get) => ({
  // State
  leads: [],
  selectedLead: null,
  loading: false,
  criticalError: null,        // Only for lead data loading failures
  operationError: null,       // For non-critical operations
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  filters: {
    status: '',
    source: '',
    assignedTo: '',
    search: ''
  },
  statistics: null,

  // Actions
  fetchLeads: async (params = {}) => {
    set({ loading: true, criticalError: null });
    try {
      const { filters, pagination } = get();
      const requestParams = {
        ...filters,
        ...params,
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit
      };
      
      console.log('Fetching leads with params:', requestParams);
      const response = await leadService.getLeads(requestParams);
      console.log('Lead service response:', response);
      
      // The response structure should match what the API returns
      if (!response) {
        throw new Error('No response from server');
      }

      // Handle the response structure based on the API format
      const leads = response.data?.leads || [];
      const meta = response.meta || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      };

      set({
        leads: leads,
        pagination: {
          page: meta.page || 1,
          limit: meta.limit || 20,
          total: meta.total || 0,
          totalPages: meta.totalPages || 0
        },
        loading: false,
        criticalError: null
      });
    } catch (error) {
      console.error('Failed to fetch leads - Full error:', error);
      set({
        criticalError: error.message || 'Failed to fetch leads',
        loading: false,
        leads: [] // Ensure leads is always an array
      });
      // Don't throw error - return empty data
      return { data: { leads: [] }, meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
    }
  },

  fetchLeadById: async (id) => {
    set({ loading: true, criticalError: null });
    try {
      const response = await leadService.getLeadById(id);
      set({
        selectedLead: response.data.lead,
        loading: false
      });
    } catch (error) {
      set({
        criticalError: error.message,
        loading: false
      });
      console.error('Failed to fetch lead details:', error);
      // Don't throw error - return null
      return null;
    }
  },

  createLead: async (leadData) => {
    set({ operationError: null });
    try {
      const response = await leadService.createLead(leadData);
      const { leads } = get();
      set({
        leads: [response.data.lead, ...leads]
      });
      return response.data.lead;
    } catch (error) {
      console.error('Failed to create lead:', error);
      set({ operationError: error.message });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to create lead',
        type: 'error'
      });
      
      throw error;
    }
  },

  updateLead: async (id, leadData) => {
    set({ operationError: null });
    try {
      const response = await leadService.updateLead(id, leadData);
      const { leads, selectedLead } = get();
      
      // Update in leads list
      const updatedLeads = leads.map(lead => 
        lead._id === id ? response.data.lead : lead
      );
      
      set({
        leads: updatedLeads,
        selectedLead: selectedLead?._id === id ? response.data.lead : selectedLead
      });
      
      return response.data.lead;
    } catch (error) {
      console.error('Failed to update lead:', error);
      set({ operationError: error.message });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to update lead',
        type: 'error'
      });
      
      throw error;
    }
  },

  deleteLead: async (id) => {
    set({ operationError: null });
    try {
      await leadService.deleteLead(id);
      const { leads, selectedLead } = get();
      
      set({
        leads: leads.filter(lead => lead._id !== id),
        selectedLead: selectedLead?._id === id ? null : selectedLead
      });
    } catch (error) {
      console.error('Failed to delete lead:', error);
      set({ operationError: error.message });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to delete lead',
        type: 'error'
      });
      
      throw error;
    }
  },

  addComment: async (leadId, commentData) => {
    set({ operationError: null });
    try {
      const response = await leadService.addComment(leadId, commentData);
      const { selectedLead } = get();
      
      if (selectedLead?._id === leadId) {
        set({ selectedLead: response.data.lead });
      }
      
      return response.data.lead;
    } catch (error) {
      console.error('Failed to add comment:', error);
      set({ operationError: error.message });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to add comment',
        type: 'error'
      });
      
      throw error;
    }
  },

  convertToClient: async (leadId, clientData) => {
    set({ operationError: null });
    try {
      const response = await leadService.convertToClient(leadId, clientData);
      const { leads, selectedLead } = get();
      
      // Update lead in list
      const updatedLeads = leads.map(lead => 
        lead._id === leadId ? response.data.lead : lead
      );
      
      set({
        leads: updatedLeads,
        selectedLead: selectedLead?._id === leadId ? response.data.lead : selectedLead
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to convert lead:', error);
      set({ operationError: error.message });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to convert lead',
        type: 'error'
      });
      
      throw error;
    }
  },

  fetchStatistics: async () => {
    try {
      const response = await leadService.getLeadStatistics();
      set({ statistics: response.data.statistics });
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      // Statistics are non-critical, just log the error
    }
  },

  setFilters: (filters) => {
    const currentFilters = get().filters;
    set({
      filters: { ...currentFilters, ...filters },
      pagination: { ...get().pagination, page: 1 } // Reset to first page
    });
  },

  setPagination: (page) => {
    set({
      pagination: { ...get().pagination, page }
    });
  },

  clearSelectedLead: () => {
    set({ selectedLead: null });
  },

  reset: () => {
    set({
      leads: [],
      selectedLead: null,
      loading: false,
      criticalError: null,
      operationError: null,
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      },
      filters: {
        status: '',
        source: '',
        assignedTo: '',
        search: ''
      },
      statistics: null
    });
  }
}));

export default useLeadStore;